/* Bold Portfolio — oversized varied-size tiles, full-width accent stripes
   between rows, uppercase Manrope captions. NO serif italic. */

import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";

const ROW_LAYOUTS = [
  ["sm:col-span-8 aspect-[16/10]", "sm:col-span-4 aspect-[3/4]"],
  ["sm:col-span-12 aspect-[21/9]"],
  ["sm:col-span-5 aspect-[3/4]", "sm:col-span-7 aspect-[16/10]"],
  ["sm:col-span-6 aspect-square", "sm:col-span-6 aspect-square"],
];

export default function BoldPortfolio() {
  const { settings } = useSiteSettings();
  const portfolio = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];

  // Slice into rows of 1-2 according to layout pattern
  const rows = [];
  let idx = 0;
  let layoutIdx = 0;
  while (idx < portfolio.length) {
    const layout = ROW_LAYOUTS[layoutIdx % ROW_LAYOUTS.length];
    rows.push({ layout, items: portfolio.slice(idx, idx + layout.length) });
    idx += layout.length;
    layoutIdx += 1;
  }

  return (
    <div className="bg-[#0E0D0C] text-[#FAF9F6] min-h-screen" data-testid="bold-portfolio" style={{ fontFamily: "var(--tenant-sans)" }}>
      <section className="pt-20 pb-12 px-6 lg:px-12 max-w-[1480px] mx-auto">
        <div className="text-[10px] uppercase tracking-[0.45em] text-[color:var(--tenant-accent)] mb-3">
          The File · {portfolio.length} plates
        </div>
        <h1 className="text-6xl lg:text-9xl font-extrabold uppercase leading-[0.92] tracking-tighter" style={{ fontFamily: "var(--tenant-sans)" }}>
          Portfolio
        </h1>
      </section>

      {portfolio.length === 0 ? (
        <p className="text-center uppercase tracking-[0.32em] text-[#FAF9F6]/55 py-32">
          No plates filed yet — start a gallery in your studio.
        </p>
      ) : (
        rows.map((row, ri) => (
          <div key={ri}>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-0">
              {row.items.map((src, i) => (
                <figure key={`${src}-${ri}-${i}`} className={`relative overflow-hidden bg-[#0E0D0C] ${row.layout[i]}`}>
                  <img src={optimizeImageUrl(src, { w: 1600 })}
                    srcSet={optimizedSrcSet(src, [800, 1200, 1600, 2000]) || undefined}
                    sizes="(max-width: 640px) 100vw, 50vw"
                    alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" style={getFocalStyle(focals, i)} />
                  <figcaption className="absolute left-5 bottom-5 text-[10px] uppercase tracking-[0.32em] text-white/85">
                    Plate {String(ri * 2 + i + 1).padStart(2, "0")}
                  </figcaption>
                </figure>
              ))}
            </div>
            {ri < rows.length - 1 && (
              <div
                className="w-full"
                style={{ height: 16, background: ri % 2 === 0 ? "var(--tenant-accent, #C9A961)" : "#FAF9F6" }}
                aria-hidden
              />
            )}
          </div>
        ))
      )}
    </div>
  );
}
